import { useState, useEffect } from 'react';
import { Card, Button, PageHeader, Input } from '../components/ui';
import { clsx } from 'clsx';
import { User, Bell, CreditCard, Shield, Trash2, BookOpen, Plus, Edit2, Check, X, ToggleLeft, ToggleRight, MessageSquare } from 'lucide-react';
import { knowledgeBaseApi, KBExample, KBCategory, CreateKBExampleInput } from '../lib/api';
import { TemplatesSection } from './TemplatesSection';

const KB_CATEGORY_LABELS: Record<KBCategory, string> = {
  REVIEW_RESPONSE: 'Ответы на отзывы',
  ARTICLE: 'Статьи',
  PROMOTION: 'Акции',
  HEADLINE: 'Заголовки',
  NEWS: 'Новости',
  SERVICES: 'Услуги',
};

const KB_CATEGORIES = Object.keys(KB_CATEGORY_LABELS) as KBCategory[];

const emptyForm: CreateKBExampleInput = {
  category: 'REVIEW_RESPONSE',
  title: '',
  userInput: '',
  aiOutput: '',
  isActive: true,
};

function KnowledgeBaseSection() {
  const [examples, setExamples] = useState<KBExample[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState<CreateKBExampleInput>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<CreateKBExampleInput>>({});
  const [error, setError] = useState('');

  useEffect(() => {
    knowledgeBaseApi.getAll().then((res) => setExamples(res.data.data)).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.userInput.trim() || !form.aiOutput.trim()) {
      setError('Заполните все поля');
      return;
    }
    try {
      const res = await knowledgeBaseApi.create(form);
      setExamples((prev) => [...prev, res.data.data]);
      setForm(emptyForm);
      setShowAddForm(false);
      setError('');
    } catch {
      setError('Не удалось создать пример');
    }
  };

  const handleEditSave = async (id: string) => {
    try {
      const res = await knowledgeBaseApi.update(id, editForm);
      setExamples((prev) => prev.map((e) => (e.id === id ? res.data.data : e)));
      setEditingId(null);
    } catch {
      setError('Не удалось сохранить');
    }
  };

  const handleToggleActive = async (example: KBExample) => {
    try {
      const res = await knowledgeBaseApi.update(example.id, { isActive: !example.isActive });
      setExamples((prev) => prev.map((e) => (e.id === example.id ? res.data.data : e)));
    } catch {
      setError('Не удалось изменить статус');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить пример из базы знаний?')) return;
    try {
      await knowledgeBaseApi.delete(id);
      setExamples((prev) => prev.filter((e) => e.id !== id));
    } catch {
      setError('Не удалось удалить');
    }
  };

  const grouped = KB_CATEGORIES.reduce<Record<KBCategory, KBExample[]>>(
    (acc, cat) => {
      acc[cat] = examples.filter((e) => e.category === cat);
      return acc;
    },
    {} as Record<KBCategory, KBExample[]>,
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {!showAddForm && (
        <Button
          variant="outline"
          size="sm"
          icon={<Plus className="w-3.5 h-3.5" />}
          onClick={() => setShowAddForm(true)}
        >
          Добавить пример
        </Button>
      )}

      {/* Add form */}
      {showAddForm && (
        <Card className="bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Новый пример</h4>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Категория</label>
              <select
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value as KBCategory }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              >
                {KB_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{KB_CATEGORY_LABELS[cat]}</option>
                ))}
              </select>
            </div>
            <Input
              label="Название *"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Короткое название для интерфейса"
            />
          </div>
          <div className="space-y-3 mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Пример входных данных (бриф) *</label>
              <textarea
                value={form.userInput}
                onChange={(e) => setForm((p) => ({ ...p, userInput: e.target.value }))}
                rows={2}
                placeholder="Что пользователь написал в бриф..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Пример качественного AI-ответа *</label>
              <textarea
                value={form.aiOutput}
                onChange={(e) => setForm((p) => ({ ...p, aiOutput: e.target.value }))}
                rows={4}
                placeholder="Идеальный ответ, который AI должен генерировать..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate}>
              Сохранить
            </Button>
            <Button
              variant="outline"
              onClick={() => { setShowAddForm(false); setForm(emptyForm); setError(''); }}
            >
              Отмена
            </Button>
          </div>
        </Card>
      )}

      {/* Grouped list */}
      {KB_CATEGORIES.map((cat) => {
        const catExamples = grouped[cat];
        return (
          <div key={cat}>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {KB_CATEGORY_LABELS[cat]} ({catExamples.length})
            </h4>
            {catExamples.length === 0 ? (
              <p className="text-xs text-gray-400 mb-3">Нет примеров</p>
            ) : (
              <div className="space-y-2 mb-3">
                {catExamples.map((ex) => (
                  <div
                    key={ex.id}
                    className={clsx(
                      'border rounded-xl p-3 shadow-card',
                      ex.isActive ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60',
                    )}
                  >
                    {editingId === ex.id ? (
                      <div className="space-y-2">
                        <Input
                          value={editForm.title ?? ex.title}
                          onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                        />
                        <textarea
                          value={editForm.userInput ?? ex.userInput}
                          onChange={(e) => setEditForm((p) => ({ ...p, userInput: e.target.value }))}
                          rows={2}
                          className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-primary-500"
                        />
                        <textarea
                          value={editForm.aiOutput ?? ex.aiOutput}
                          onChange={(e) => setEditForm((p) => ({ ...p, aiOutput: e.target.value }))}
                          rows={3}
                          className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Check className="w-4 h-4 text-green-600" />}
                            onClick={() => handleEditSave(ex.id)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<X className="w-4 h-4 text-gray-400" />}
                            onClick={() => setEditingId(null)}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">{ex.title}</p>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">Бриф: {ex.userInput}</p>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            title={ex.isActive ? 'Деактивировать' : 'Активировать'}
                            onClick={() => handleToggleActive(ex)}
                            icon={ex.isActive ? <ToggleRight className="w-4 h-4 text-primary-500" /> : <ToggleLeft className="w-4 h-4" />}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingId(ex.id); setEditForm({}); }}
                            icon={<Edit2 className="w-3.5 h-3.5" />}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(ex.id)}
                            icon={<Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function Settings() {
  return (
    <div>
      <PageHeader
        title="Настройки"
        subtitle="Управление аккаунтом и настройками"
      />

      <div className="p-6 space-y-6 max-w-3xl">
        {/* Profile */}
        <Card>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary-100 rounded-lg flex-shrink-0">
              <User className="w-6 h-6 text-primary-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Профиль</h3>
              <p className="text-sm text-gray-500 mt-1">Ваши персональные данные</p>
              <div className="mt-4 space-y-4">
                <Input
                  label="Имя"
                  type="text"
                  defaultValue="Пользователь"
                />
                <Input
                  label="Email"
                  type="email"
                  defaultValue="user@example.com"
                />
                <Button>Сохранить</Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Security */}
        <Card>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Безопасность</h3>
              <p className="text-sm text-gray-500 mt-1">Пароль и авторизация</p>
              <Button variant="outline" className="mt-4">
                Сменить пароль
              </Button>
            </div>
          </div>
        </Card>

        {/* Notifications */}
        <Card>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary-100 rounded-lg flex-shrink-0">
              <Bell className="w-6 h-6 text-primary-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Уведомления</h3>
              <p className="text-sm text-gray-500 mt-1">Настройка email-уведомлений</p>
              <div className="mt-4 space-y-3">
                <label className="flex items-center gap-3">
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-primary-600 rounded" />
                  <span className="text-sm text-gray-700">Новые отзывы</span>
                </label>
                <label className="flex items-center gap-3">
                  <input type="checkbox" defaultChecked className="w-4 h-4 text-primary-600 rounded" />
                  <span className="text-sm text-gray-700">Еженедельные отчёты</span>
                </label>
                <label className="flex items-center gap-3">
                  <input type="checkbox" className="w-4 h-4 text-primary-600 rounded" />
                  <span className="text-sm text-gray-700">Маркетинговые рассылки</span>
                </label>
              </div>
            </div>
          </div>
        </Card>

        {/* Subscription */}
        <Card>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 rounded-lg flex-shrink-0">
              <CreditCard className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Подписка</h3>
              <p className="text-sm text-gray-500 mt-1">Текущий тариф: <span className="font-medium text-gray-900">Free</span></p>
              <Button className="mt-4">Перейти на Pro</Button>
            </div>
          </div>
        </Card>

        {/* Knowledge Base */}
        <Card>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg flex-shrink-0">
              <BookOpen className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-gray-900">База знаний</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Шаблоны и примеры для улучшения качества AI-генерации (few-shot prompting).
                Добавляйте образцовые тексты — AI будет ориентироваться на них при генерации.
              </p>
              <KnowledgeBaseSection />
            </div>
          </div>
        </Card>

        {/* Review response templates */}
        <Card>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-indigo-100 rounded-lg flex-shrink-0">
              <MessageSquare className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-gray-900">Шаблоны ответов</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Готовые шаблоны ответов на отзывы по тональности. Используются при генерации ответов на отзывы.
              </p>
              <TemplatesSection />
            </div>
          </div>
        </Card>

        {/* Danger zone */}
        <Card className="border-red-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 rounded-lg flex-shrink-0">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Опасная зона</h3>
              <p className="text-sm text-gray-500 mt-1">Удаление аккаунта и всех данных</p>
              <Button variant="danger" className="mt-4">
                Удалить аккаунт
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
